import { useMutation, useQuery, useQueryClient, type UseQueryOptions } from "@tanstack/react-query";
import { organisationService } from "@/services/organisationService";
import type {
  Organisation,
  OrganisationAction,
  OrganisationContact,
  OrganisationGeofence,
  OrganisationMember,
  OrganisationRole,
  UpdateOrganisationMemberRoleInput,
} from "@/types/organisation";

export const organisationKeys = {
  all: ["organisations"] as const,
  lists: () => [...organisationKeys.all, "list"] as const,
  list: (filters: unknown) => [...organisationKeys.lists(), { filters }] as const,
  details: () => [...organisationKeys.all, "detail"] as const,
  detail: (id: number | string | undefined) => [...organisationKeys.details(), id] as const,
  members: (id: number | string | undefined) => [...organisationKeys.all, "members", id] as const,
  contacts: (id: number | string | undefined) => [...organisationKeys.all, "contacts", id] as const,
  geofences: (id: number | string | undefined) =>
    [...organisationKeys.all, "geofences", id] as const,
  actions: (id: number | string | undefined) => [...organisationKeys.all, "actions", id] as const,
  roles: (id: number | string | undefined) => [...organisationKeys.all, "roles", id] as const,
};

interface QueryOpts {
  enabled?: boolean;
}

export const useOrganisations = (options: Partial<UseQueryOptions<Organisation[]>> = {}) => {
  return useQuery({
    queryKey: organisationKeys.lists(),
    queryFn: organisationService.getOrganisations,
    ...options,
  });
};

export const useOrganisation = (
  id: number | string | undefined,
  options: Partial<UseQueryOptions<Organisation>> = {},
) => {
  return useQuery({
    queryKey: organisationKeys.detail(id),
    queryFn: () => organisationService.getOrganisation(id as number | string),
    enabled: !!id,
    ...options,
  });
};

export const useOrganisationMembers = (
  id: number | string | undefined,
  options: QueryOpts & Partial<UseQueryOptions<OrganisationMember[]>> = {},
) => {
  return useQuery({
    queryKey: organisationKeys.members(id),
    queryFn: () => organisationService.getOrganisationMembers(id as number | string),
    enabled: !!id,
    ...options,
  });
};

export const useOrganisationContacts = (
  id: number | string | undefined,
  options: QueryOpts & Partial<UseQueryOptions<OrganisationContact[]>> = {},
) => {
  return useQuery({
    queryKey: organisationKeys.contacts(id),
    queryFn: () => organisationService.getOrganisationContacts(id as number | string),
    enabled: !!id,
    ...options,
  });
};

export const useOrganisationGeofences = (
  id: number | string | undefined,
  options: QueryOpts & Partial<UseQueryOptions<OrganisationGeofence[]>> = {},
) => {
  return useQuery({
    queryKey: organisationKeys.geofences(id),
    queryFn: () => organisationService.getOrganisationGeofences(id as number | string),
    enabled: !!id,
    ...options,
  });
};

export const useOrganisationActions = (
  id: number | string | undefined,
  options: QueryOpts & Partial<UseQueryOptions<OrganisationAction[]>> = {},
) => {
  return useQuery({
    queryKey: organisationKeys.actions(id),
    queryFn: () => organisationService.getOrganisationActions(id as number | string),
    enabled: !!id,
    ...options,
  });
};

export const useCreateOrganisation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: organisationService.createOrganisation,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: organisationKeys.lists() });
    },
  });
};

export const useUpdateOrganisation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: number | string; data: Partial<Organisation> }) =>
      organisationService.updateOrganisation(id, data),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: organisationKeys.lists() });
      queryClient.invalidateQueries({ queryKey: organisationKeys.detail(variables.id) });
    },
  });
};

export const useDeleteOrganisation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: organisationService.deleteOrganisation,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: organisationKeys.lists() });
    },
  });
};

export const useDeleteOrganisationMember = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      organisationId,
      userId,
    }: {
      organisationId: number | string;
      userId: number | string;
    }) => organisationService.deleteOrganisationMember(organisationId, userId),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: organisationKeys.members(variables.organisationId) });
    },
  });
};

export const useUpdateOrganisationMembers = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      organisationId,
      members,
    }: {
      organisationId: number | string;
      members: (number | string)[];
    }) => organisationService.updateOrganisationMembers(organisationId, members),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: organisationKeys.members(variables.organisationId) });
    },
  });
};

export const useChangeOrganisationOwner = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      organisationId,
      ownerId,
    }: {
      organisationId: number | string;
      ownerId: number | string;
    }) => organisationService.changeOrganisationOwner(organisationId, ownerId),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: organisationKeys.lists() });
      queryClient.invalidateQueries({ queryKey: organisationKeys.detail(variables.organisationId) });
      queryClient.invalidateQueries({ queryKey: organisationKeys.members(variables.organisationId) });
    },
  });
};

export const useChangeOrganisationName = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      organisationId,
      name,
    }: {
      organisationId: number | string;
      name: string;
    }) => organisationService.changeOrganisationName(organisationId, name),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: organisationKeys.lists() });
      queryClient.invalidateQueries({ queryKey: organisationKeys.detail(variables.organisationId) });
    },
  });
};

export const useOrganisationRoles = (
  id: number | string | undefined,
  options: QueryOpts & Partial<UseQueryOptions<OrganisationRole[]>> = {},
) => {
  return useQuery({
    queryKey: organisationKeys.roles(id),
    queryFn: () => organisationService.getOrganisationRoles(id as number | string),
    enabled: !!id,
    ...options,
  });
};

export const useUpdateOrganisationMemberRole = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      organisationId,
      userId,
      roleData,
    }: {
      organisationId: number | string;
      userId: number | string;
      roleData: UpdateOrganisationMemberRoleInput;
    }) => organisationService.updateOrganisationMemberRole(organisationId, userId, roleData),
    onSuccess: (_data, variables) => {
      const orgId = parseInt(String(variables.organisationId));

      queryClient.invalidateQueries({ queryKey: organisationKeys.roles(orgId) });
      queryClient.invalidateQueries({ queryKey: organisationKeys.members(orgId) });
      queryClient.invalidateQueries({ queryKey: organisationKeys.lists() });
      queryClient.invalidateQueries({ queryKey: ["organisations", "roles"], exact: false });
    },
  });
};
